import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import facade from "../services/qwof/apiFacade";
import NavigationRow from "../components/NavigationRow.jsx";
import Spinner from "../components/Spinner.jsx";
import ItemSelectWindow from "../components/ItemSelectWindow.jsx";
import QuagganEmoji from "../components/QuagganEmoji.jsx";

function getGameId(game) {
  const candidates = [game?.id, game?.gameId, game?.ID];
  const resolved = candidates.find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");
  return resolved === undefined ? "" : String(resolved);
}

function GamePage() {
  const { isLoggedIn, username } = useAuth();
  const { id: routeGameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const nextLocalIdRef = useRef(1);

  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [createdByUsername, setCreatedByUsername] = useState("");
  const [participants, setParticipants] = useState([]);
  const [results, setResults] = useState([]);
  const [selectionTarget, setSelectionTarget] = useState(null);

  const createLocalId = (prefix) => `${prefix}-${nextLocalIdRef.current++}`;

  useEffect(() => {
    const { documentElement, body } = document;
    const previousHtmlOverflowX = documentElement.style.overflowX;
    const previousHtmlOverflowY = documentElement.style.overflowY;
    const previousHtmlHeight = documentElement.style.height;
    const previousBodyOverflowX = body.style.overflowX;
    const previousBodyOverflowY = body.style.overflowY;
    const previousBodyHeight = body.style.height;

    documentElement.style.overflowX = "hidden";
    documentElement.style.overflowY = "auto";
    documentElement.style.height = "auto";
    body.style.overflowX = "hidden";
    body.style.overflowY = "auto";
    body.style.height = "auto";

    return () => {
      documentElement.style.overflowX = previousHtmlOverflowX;
      documentElement.style.overflowY = previousHtmlOverflowY;
      documentElement.style.height = previousHtmlHeight;
      body.style.overflowX = previousBodyOverflowX;
      body.style.overflowY = previousBodyOverflowY;
      body.style.height = previousBodyHeight;
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/");
      return;
    }

    const allowed = facade.hasAnyRole(["ADMIN", "LEADER"]);
    if (!allowed) {
      navigate("/");
      return;
    }

    setHasAccess(true);
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!hasAccess) return;

    let cancelled = false;

    const normalizeGame = (game) => {
      nextLocalIdRef.current = 1;

      const normalizedParticipants = [];
      const participantIdByName = new Map();

      const ensureParticipant = (displayName) => {
        const safeName = String(displayName || "").trim();
        const lookupKey = safeName.toLowerCase();

        if (participantIdByName.has(lookupKey)) {
          return participantIdByName.get(lookupKey);
        }

        const localId = createLocalId("participant");
        participantIdByName.set(lookupKey, localId);
        normalizedParticipants.push({ localId, displayName: safeName });
        return localId;
      };

      (game.participants || []).forEach((participant) => {
        ensureParticipant(participant?.displayName || participant?.name || "");
      });

      const normalizedResults = (game.results || []).map((result) => ({
        localId: createLocalId("result"),
        participantLocalId: ensureParticipant(result?.participantName || ""),
        item: result?.item || null,
        quantity: Math.max(1, Number(result?.quantity) || 1),
      }));

      return {
        title: String(game.title || ""),
        createdByUsername: String(game.createdByUsername || username || ""),
        participants: normalizedParticipants,
        results: normalizedResults,
      };
    };

    const loadGame = async () => {
      setLoading(true);
      setError("");
      setMessage("");

      try {
        const routeGame = location.state?.game;
        let game = routeGame && getGameId(routeGame) === String(routeGameId) ? routeGame : null;

        if (!game) {
          const games = await facade.getGames();
          if (Array.isArray(games)) {
            game = games.find((entry) => getGameId(entry) === String(routeGameId)) || null;
          }
        }

        if (cancelled) return;

        if (!game) {
          setError("Game not found.");
          return;
        }

        const normalizedGame = normalizeGame(game);
        setTitle(normalizedGame.title);
        setCreatedByUsername(normalizedGame.createdByUsername);
        setParticipants(normalizedGame.participants);
        setResults(normalizedGame.results);
      } catch (err) {
        if (cancelled) return;
        const messageText = err?.fullError || err?.message || "Unknown error";
        setError(`Failed to load game: ${messageText}`);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGame();

    return () => {
      cancelled = true;
    };
  }, [hasAccess, location.state, routeGameId, username]);

  const activeResult = selectionTarget
    ? results.find((result) => result.localId === selectionTarget.resultLocalId) || null
    : null;

  const handleParticipantChange = (localId, value) => {
    setParticipants((previous) =>
      previous.map((participant) =>
        participant.localId === localId
          ? { ...participant, displayName: value }
          : participant,
      ),
    );
  };

  const handleAddParticipant = () => {
    setParticipants((previous) => [...previous, { localId: createLocalId("participant"), displayName: "" }]);
  };

  const handleRemoveParticipant = (participantLocalId) => {
    setParticipants((previous) => previous.filter((participant) => participant.localId !== participantLocalId));
    setResults((previous) => previous.filter((result) => result.participantLocalId !== participantLocalId));

    if (selectionTarget?.type === "participant" && activeResult?.participantLocalId === participantLocalId) {
      setSelectionTarget(null);
    }
  };

  const handleAddResult = () => {
    let defaultParticipantId = participants[0]?.localId;

    if (!defaultParticipantId) {
      defaultParticipantId = createLocalId("participant");
      setParticipants([{ localId: defaultParticipantId, displayName: "" }]);
    }

    setResults((previous) => [
      ...previous,
      {
        localId: createLocalId("result"),
        participantLocalId: defaultParticipantId,
        item: null,
        quantity: 1,
      },
    ]);
  };

  const handleRemoveResult = (resultLocalId) => {
    setResults((previous) => previous.filter((result) => result.localId !== resultLocalId));

    if (selectionTarget?.resultLocalId === resultLocalId) {
      setSelectionTarget(null);
    }
  };

  const handleQuantityChange = (resultLocalId, value) => {
    setResults((previous) =>
      previous.map((result) =>
        result.localId === resultLocalId
          ? { ...result, quantity: Math.max(1, Number(value) || 1) }
          : result,
      ),
    );
  };

  const handleSelectParticipant = (participant) => {
    if (!selectionTarget?.resultLocalId) return;

    setResults((previous) =>
      previous.map((result) =>
        result.localId === selectionTarget.resultLocalId
          ? { ...result, participantLocalId: participant.localId }
          : result,
      ),
    );
    setSelectionTarget(null);
  };

  const handleSelectItem = (item, quantity = 1) => {
    if (!selectionTarget?.resultLocalId) return;

    setResults((previous) =>
      previous.map((result) =>
        result.localId === selectionTarget.resultLocalId
          ? { ...result, item, quantity: Math.max(1, Number(quantity) || 1) }
          : result,
      ),
    );
    setSelectionTarget(null);
  };

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    const normalizedParticipants = participants.map((participant) => ({
      ...participant,
      displayName: participant.displayName.trim(),
    }));

    if (!trimmedTitle) {
      setError("Game title is required.");
      setMessage("");
      return;
    }

    if (normalizedParticipants.length === 0) {
      setError("Add at least one participant.");
      setMessage("");
      return;
    }

    if (normalizedParticipants.some((participant) => !participant.displayName)) {
      setError("Participant names cannot be empty.");
      setMessage("");
      return;
    }

    if (results.length === 0) {
      setError("Add at least one result.");
      setMessage("");
      return;
    }

    const participantNameById = new Map(
      normalizedParticipants.map((participant) => [participant.localId, participant.displayName]),
    );

    const invalidResult = results.find(
      (result) => !participantNameById.get(result.participantLocalId) || !result.item?.id,
    );

    if (invalidResult) {
      setError("Each result must have a winner and an item.");
      setMessage("");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const uniqueParticipants = [];
      const seenParticipants = new Set();

      normalizedParticipants.forEach((participant) => {
        const lookupKey = participant.displayName.toLowerCase();
        if (seenParticipants.has(lookupKey)) return;
        seenParticipants.add(lookupKey);
        uniqueParticipants.push({ displayName: participant.displayName });
      });

      const payload = {
        createdByUsername: createdByUsername || username || "",
        title: trimmedTitle,
        participants: uniqueParticipants,
        results: results.map((result) => ({
          participantName: participantNameById.get(result.participantLocalId),
          itemId: result.item.id,
          quantity: Math.max(1, Number(result.quantity) || 1),
        })),
      };

      await facade.updateGame(routeGameId, payload);

      setParticipants(normalizedParticipants);
      setTitle(trimmedTitle);
      setCreatedByUsername(payload.createdByUsername);
      setMessage("Game updated.");
    } catch (err) {
      const messageText = err?.fullError || err?.message || "Unknown error";
      setError(`Failed to update game: ${messageText}`);
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="admin-route">
      <NavigationRow className="admin-nav-row" />
      <div className="admin-route-inner">
        <div className="admin-panel">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 className="register-title" style={{ textAlign: "left", marginBottom: 6 }}>
                Edit Game
              </h2>
              <p className="login-help-text" style={{ marginTop: 0 }}>
                Update participants, winners, and prize assignments for this game.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="names-btn secondary" onClick={() => navigate("/admin/history")}>
                Back to history
              </button>
              <button type="button" className="names-btn primary" onClick={handleSave} disabled={loading || saving}>
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>

          {loading ? (
            <>
              <Spinner />
              <p className="login-help-text" style={{ textAlign: "center", marginTop: -8 }}>
                Loading game
              </p>
            </>
          ) : null}

          {error ? (
            <>
              <pre
                className="register-error"
                style={{
                  whiteSpace: "pre-wrap",
                  marginTop: 8,
                  background: "rgba(120, 20, 20, 0.5)",
                  padding: 10,
                  borderRadius: 10,
                }}
              >
                {error}
              </pre>
              <QuagganEmoji type="failure" />
            </>
          ) : null}

          {message && !error ? (
            <>
              <div
                className="login-help-text"
                style={{
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(0, 74, 110, 0.42)",
                  color: "#b9f1ff",
                }}
              >
                {message}
              </div>
              <QuagganEmoji type="success" />
            </>
          ) : null}

          {!loading ? (
            <div style={{ display: "grid", gap: 18, marginTop: 18 }}>
              <section
                style={{
                  borderRadius: 18,
                  padding: 18,
                  background: "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                  border: "1px solid rgba(80, 180, 255, 0.24)",
                }}
              >
                <div style={{ fontWeight: 800, color: "#40e8ff", marginBottom: 12, fontSize: 18 }}>
                  Details
                </div>
                <input
                  type="text"
                  className="login-input"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Game title"
                  style={{ maxWidth: 560, marginBottom: 12 }}
                />
                <div className="login-help-text" style={{ marginTop: 0 }}>
                  Created by <strong>{createdByUsername || username}</strong>
                </div>
              </section>

              <section
                style={{
                  borderRadius: 18,
                  padding: 18,
                  background: "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                  border: "1px solid rgba(80, 180, 255, 0.24)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#40e8ff", fontSize: 18 }}>Participants</div>
                  <button type="button" className="names-btn primary" onClick={handleAddParticipant}>
                    Add participant
                  </button>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {participants.map((participant, index) => (
                    <div
                      key={participant.localId}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 10,
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        className="login-input"
                        value={participant.displayName}
                        onChange={(event) => handleParticipantChange(participant.localId, event.target.value)}
                        placeholder={`Participant ${index + 1}`}
                        style={{ marginBottom: 0 }}
                      />
                      <button
                        type="button"
                        className="names-btn secondary"
                        onClick={() => handleRemoveParticipant(participant.localId)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section
                style={{
                  borderRadius: 18,
                  padding: 18,
                  background: "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                  border: "1px solid rgba(80, 180, 255, 0.24)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#40e8ff", fontSize: 18 }}>Results</div>
                  <button type="button" className="names-btn primary" onClick={handleAddResult}>
                    Add result
                  </button>
                </div>

                <p className="login-help-text" style={{ marginTop: 0, marginBottom: 14 }}>
                  Click a winner or item to change it in a popup window.
                </p>

                <div style={{ display: "grid", gap: 12 }}>
                  {results.map((result, index) => {
                    const selectedParticipant = participants.find(
                      (participant) => participant.localId === result.participantLocalId,
                    );
                    const itemName = result.item?.name || "Select item";

                    return (
                      <div
                        key={result.localId}
                        style={{
                          borderRadius: 16,
                          padding: 16,
                          background: "rgba(6, 20, 42, 0.82)",
                          border: "1px solid rgba(80, 180, 255, 0.18)",
                          display: "grid",
                          gap: 12,
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#9be7ff" }}>Result {index + 1}</div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(214,232,255,0.66)", marginBottom: 6 }}>
                              Winner
                            </div>
                            <button
                              type="button"
                              className="names-btn secondary"
                              onClick={() => setSelectionTarget({ type: "participant", resultLocalId: result.localId })}
                              style={{ width: "100%", textAlign: "left", justifyContent: "flex-start", borderRadius: 12, padding: "10px 14px" }}
                            >
                              {selectedParticipant?.displayName || "Select winner"}
                            </button>
                          </div>

                          <div>
                            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(214,232,255,0.66)", marginBottom: 6 }}>
                              Item
                            </div>
                            <button
                              type="button"
                              className="names-btn secondary"
                              onClick={() => setSelectionTarget({ type: "item", resultLocalId: result.localId })}
                              style={{ width: "100%", textAlign: "left", justifyContent: "flex-start", borderRadius: 12, padding: "10px 14px" }}
                            >
                              {itemName}
                            </button>
                          </div>

                          <div>
                            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(214,232,255,0.66)", marginBottom: 6 }}>
                              Quantity
                            </div>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              className="login-input"
                              value={result.quantity}
                              onChange={(event) => handleQuantityChange(result.localId, event.target.value)}
                              style={{ marginBottom: 0 }}
                            />
                          </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button type="button" className="names-btn secondary" onClick={() => handleRemoveResult(result.localId)}>
                            Remove result
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>

      <ItemSelectWindow
        visible={!!selectionTarget}
        isLoggedIn={isLoggedIn}
        mode={selectionTarget?.type === "participant" ? "participant" : "item"}
        selectionMode="single"
        participants={participants}
        selectedParticipantId={activeResult?.participantLocalId || ""}
        selectedItemId={activeResult?.item?.id ?? null}
        onClose={() => setSelectionTarget(null)}
        onSelectParticipant={handleSelectParticipant}
        onSelectItem={handleSelectItem}
      />
    </div>
  );
}

export default GamePage;